(() => {
  const STORAGE_KEY = 'wishlist';
  const UPDATE_EVENT = 'hipeon:wishlist:updated';
  const PAGE_SELECTOR = '[data-wishlist-page]';
  const COUNT_SELECTOR = '[data-wishlist-count], #WishlistCount';
  const TOGGLE_SELECTOR = '[data-wishlist-toggle], .wishlist-button';
  const DEFAULT_ADD_LABEL = 'Add to wishlist';
  const DEFAULT_REMOVE_LABEL = 'Remove from wishlist';

  let wishlistItems = [];
  let pageRenderFrame = null;
  let pageFeedbackMessage = '';

  const normalizeHandle = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    if (!normalized || normalized === 'undefined' || normalized === 'null') return '';
    return normalized;
  };

  const uniqueHandles = (items) => {
    const unique = [];
    items.forEach((item) => {
      const handle = normalizeHandle(item);
      if (handle && !unique.includes(handle)) unique.push(handle);
    });
    return unique;
  };

  const readStorage = () => {
    try {
      const rawValue = window.localStorage.getItem(STORAGE_KEY);
      if (!rawValue) return [];
      const parsed = JSON.parse(rawValue);

      if (!Array.isArray(parsed)) return [];

      return uniqueHandles(
        parsed.map((item) => {
          if (typeof item === 'string' || typeof item === 'number') return item;
          if (item && typeof item === 'object') return item.handle || item.id || item.productHandle || '';
          return '';
        })
      );
    } catch (error) {
      console.warn('Wishlist: unable to read localStorage', error);
      return [];
    }
  };

  const dispatchUpdate = () => {
    document.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, {
        detail: {
          items: [...wishlistItems],
          count: wishlistItems.length,
        },
      })
    );
  };

  const syncCountBadges = (root = document) => {
    const count = wishlistItems.length;
    root.querySelectorAll(COUNT_SELECTOR).forEach((element) => {
      element.textContent = count;
      element.dataset.count = String(count);
    });
  };

  const getButtonHandle = (button) =>
    normalizeHandle(button.dataset.productHandle || button.dataset.productId || button.dataset.handle);

  const syncToggleButton = (button) => {
    const handle = getButtonHandle(button);
    if (!handle) return;

    const isActive = wishlistItems.includes(handle);
    const addLabel = button.dataset.wishlistLabelRemoved || DEFAULT_ADD_LABEL;
    const removeLabel = button.dataset.wishlistLabelAdded || DEFAULT_REMOVE_LABEL;
    const activeLabel = isActive ? removeLabel : addLabel;

    button.classList.toggle('active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', activeLabel);
    button.setAttribute('title', activeLabel);
  };

  const syncToggleButtons = (root = document) => {
    root.querySelectorAll(TOGGLE_SELECTOR).forEach((button) => {
      syncToggleButton(button);
    });
  };

  const queuePageRender = () => {
    if (pageRenderFrame) cancelAnimationFrame(pageRenderFrame);
    pageRenderFrame = requestAnimationFrame(() => {
      pageRenderFrame = null;
      renderWishlistPage();
    });
  };

  const syncUi = ({ renderPage = true, dispatch = false } = {}) => {
    syncCountBadges(document);
    syncToggleButtons(document);

    if (renderPage) queuePageRender();
    if (dispatch) dispatchUpdate();
  };

  const persist = (nextItems, options = {}) => {
    wishlistItems = uniqueHandles(nextItems);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (error) {
      console.warn('Wishlist: unable to write localStorage', error);
    }

    syncUi({
      renderPage: options.renderPage !== false,
      dispatch: options.dispatch !== false,
    });
  };

  const formatMoney = (cents) => {
    const locale = document.documentElement.lang || window.Shopify?.locale || 'en-GB';
    const currency = window.Shopify?.currency?.active || 'GBP';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `£${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const buildWishlistCard = (product) => {
    const title = escapeHtml(product.title);
    const vendor = escapeHtml(product.vendor || '');
    const handle = escapeHtml(product.handle);
    const productUrl = escapeHtml(product.url);
    const primaryImage = escapeHtml(product.featured_image || product.images?.[0] || '');
    const secondaryImage = escapeHtml(product.images?.[1] || '');
    const hasSecondaryImage = Boolean(secondaryImage);
    const isOnSale = Number(product.compare_at_price) > Number(product.price);
    const isSoldOut = !product.available;
    const savingsPercentage =
      isOnSale && Number(product.compare_at_price) > 0
        ? Math.round(((Number(product.compare_at_price) - Number(product.price)) / Number(product.compare_at_price)) * 100)
        : 0;
    const availabilityLabel = isSoldOut ? 'Currently unavailable' : 'Available now';
    const availabilityClass = isSoldOut ? ' wishlist-card__status--muted' : '';

    return `
      <article class="wishlist-card" data-handle="${handle}">
        <div class="wishlist-card__media-shell">
          <a href="${productUrl}" class="wishlist-card__media-link" aria-label="${title}">
            <div class="wishlist-card__media">
              ${
                primaryImage
                  ? `<img src="${primaryImage}" alt="${title}" class="wishlist-card__image" loading="lazy">`
                  : '<div class="wishlist-card__placeholder" aria-hidden="true"></div>'
              }
              ${
                hasSecondaryImage
                  ? `<img src="${secondaryImage}" alt="" class="wishlist-card__image wishlist-card__image--secondary" loading="lazy">`
                  : ''
              }
            </div>
          </a>
          <button
            type="button"
            class="wishlist-card__heart"
            data-wishlist-toggle
            data-product-handle="${handle}"
            data-wishlist-label-added="${DEFAULT_REMOVE_LABEL}"
            data-wishlist-label-removed="${DEFAULT_ADD_LABEL}"
            aria-pressed="true"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20.8 4.6c-1.6-1.5-4.1-1.5-5.7 0l-1.1 1.1-1.1-1.1c-1.6-1.5-4.1-1.5-5.7 0-1.8 1.7-1.8 4.5 0 6.2l6.8 6.7 6.8-6.7c1.7-1.7 1.7-4.5 0-6.2z"></path>
            </svg>
            <span class="visually-hidden">${DEFAULT_REMOVE_LABEL}</span>
          </button>
          ${
            isSoldOut
              ? '<span class="wishlist-card__badge wishlist-card__badge--muted">Sold out</span>'
              : isOnSale && savingsPercentage > 0
                ? `<span class="wishlist-card__badge">Save ${savingsPercentage}%</span>`
                : ''
          }
        </div>
        <div class="wishlist-card__content">
          <div class="wishlist-card__meta">
            <p class="wishlist-card__vendor">${vendor || 'Saved item'}</p>
            <span class="wishlist-card__status${availabilityClass}">${availabilityLabel}</span>
          </div>
          <h2 class="wishlist-card__title">
            <a href="${productUrl}">${title}</a>
          </h2>
          <div class="wishlist-card__price">
            ${
              isOnSale
                ? `<s class="wishlist-card__price-compare">${formatMoney(product.compare_at_price)}</s>`
                : ''
            }
            <span class="wishlist-card__price-current">${formatMoney(product.price)}</span>
          </div>
          <div class="wishlist-card__actions">
            <a href="${productUrl}" class="wishlist-card__action wishlist-card__action--primary">View product</a>
            <button
              type="button"
              class="wishlist-card__action wishlist-card__action--secondary"
              data-wishlist-toggle
              data-product-handle="${handle}"
              data-wishlist-label-added="${DEFAULT_REMOVE_LABEL}"
              data-wishlist-label-removed="${DEFAULT_ADD_LABEL}"
            >
              Remove
            </button>
          </div>
        </div>
      </article>
    `;
  };

  const fetchWishlistProduct = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 404) {
      return {
        handle,
        missing: true,
      };
    }

    if (!response.ok) {
      throw new Error(`Wishlist: failed to fetch product for handle "${handle}" (${response.status})`);
    }

    const product = await response.json();

    return {
      handle,
      product,
    };
  };

  const updatePageChrome = (page, count) => {
    const countElements = page.querySelectorAll('[data-wishlist-page-count]');
    const clearButton = page.querySelector('[data-wishlist-clear]');

    countElements.forEach((element) => {
      element.textContent = count;
    });

    if (clearButton) {
      clearButton.disabled = count === 0;
    }
  };

  const showPageMessage = (page, message) => {
    const feedback = page.querySelector('[data-wishlist-feedback]');
    if (!feedback) return;

    if (message) {
      feedback.textContent = message;
      feedback.classList.remove('hidden');
    } else {
      feedback.textContent = '';
      feedback.classList.add('hidden');
    }
  };

  const setPageLoadingState = (page, isLoading) => {
    const loader = page.querySelector('[data-wishlist-loader]');
    if (!loader) return;

    loader.classList.toggle('hidden', !isLoading);
  };

  const renderEmptyState = (page) => {
    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');

    if (grid) {
      grid.innerHTML = '';
      grid.classList.add('hidden');
    }

    if (empty) {
      empty.classList.remove('hidden');
    }
  };

  const renderGridState = (page, markup) => {
    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');

    if (grid) {
      grid.innerHTML = markup;
      grid.classList.remove('hidden');
    }

    if (empty) {
      empty.classList.add('hidden');
    }
  };

  const renderWishlistPage = async () => {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;

    const currentItems = [...wishlistItems];
    updatePageChrome(page, currentItems.length);

    if (!currentItems.length) {
      setPageLoadingState(page, false);
      showPageMessage(page, pageFeedbackMessage);
      pageFeedbackMessage = '';
      renderEmptyState(page);
      return;
    }

    setPageLoadingState(page, true);

    try {
      const results = await Promise.all(currentItems.map((handle) => fetchWishlistProduct(handle)));

      if (currentItems.join('|') !== wishlistItems.join('|')) {
        setPageLoadingState(page, false);
        queuePageRender();
        return;
      }

      const missingHandles = results.filter((result) => result.missing).map((result) => result.handle);

      if (missingHandles.length) {
        pageFeedbackMessage =
          missingHandles.length === 1
            ? '1 unavailable item was removed from your wishlist.'
            : `${missingHandles.length} unavailable items were removed from your wishlist.`;
        persist(
          currentItems.filter((handle) => !missingHandles.includes(handle)),
          {
            renderPage: true,
            dispatch: true,
          }
        );
        return;
      }

      const products = results
        .map((result) => result.product)
        .filter(Boolean);

      setPageLoadingState(page, false);
      showPageMessage(page, pageFeedbackMessage);
      pageFeedbackMessage = '';

      if (!products.length) {
        renderEmptyState(page);
        return;
      }

      renderGridState(
        page,
        products
          .map((product) => buildWishlistCard(product))
          .join('')
      );
      syncToggleButtons(page);
    } catch (error) {
      console.error(error);
      setPageLoadingState(page, false);
      showPageMessage(page, 'There was a problem loading your wishlist. Please try refreshing the page.');
      renderEmptyState(page);
    }
  };

  const api = {
    getItems() {
      return [...wishlistItems];
    },
    has(handle) {
      return wishlistItems.includes(normalizeHandle(handle));
    },
    add(handle) {
      const normalizedHandle = normalizeHandle(handle);
      if (!normalizedHandle || wishlistItems.includes(normalizedHandle)) return false;

      persist([...wishlistItems, normalizedHandle]);
      return true;
    },
    remove(handle) {
      const normalizedHandle = normalizeHandle(handle);
      if (!normalizedHandle || !wishlistItems.includes(normalizedHandle)) return false;

      persist(wishlistItems.filter((item) => item !== normalizedHandle));
      return true;
    },
    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }

      this.add(handle);
      return true;
    },
    clear() {
      persist([]);
    },
    sync(root = document) {
      syncCountBadges(root);
      syncToggleButtons(root);
    },
    refresh() {
      wishlistItems = readStorage();
      syncUi({
        renderPage: true,
        dispatch: false,
      });
    },
  };

  const onToggleClick = (event) => {
    const button = event.target.closest(TOGGLE_SELECTOR);
    if (!button) return;

    const handle = getButtonHandle(button);
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    api.toggle(handle);
  };

  const onUtilityClick = (event) => {
    const clearButton = event.target.closest('[data-wishlist-clear]');
    if (!clearButton) return;

    event.preventDefault();
    pageFeedbackMessage = 'Wishlist cleared.';
    api.clear();
  };

  const onStorageChange = (event) => {
    if (event.key !== STORAGE_KEY) return;
    wishlistItems = readStorage();
    syncUi({
      renderPage: true,
      dispatch: false,
    });
  };

  const init = () => {
    wishlistItems = readStorage();
    syncUi({
      renderPage: true,
      dispatch: false,
    });

    document.addEventListener('click', onToggleClick, true);
    document.addEventListener('click', onUtilityClick);
    document.addEventListener('shopify:section:load', () => {
      syncUi({
        renderPage: true,
        dispatch: false,
      });
    });
    window.addEventListener('storage', onStorageChange);
  };

  window.HipeonWishlist = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
